import { inject, Injectable, signal } from '@angular/core';
import { PaisEnum } from '@app/core/models';
import { StatusEnum } from '@app/core/models/status.enum';
import { AlertService, IntegrationService, UserService } from '@app/core/services';
import { Checker } from '@app/shared/utils';
import { notNull } from '@app/shared/utils/operators';
import { AppSelectors } from '@app/store';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { catchError, Observable, of } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { Builder, ShortcutBuilder } from './shortcut';

@Injectable({
  providedIn: 'root',
})
export class ShortcutStudent implements ShortcutBuilder {
  private readonly alertService = inject(AlertService);
  private readonly userService = inject(UserService);
  private readonly store = inject<Store<AppState>>(Store);
  private readonly translateService = inject(TranslateService);
  private readonly integrationService = inject(IntegrationService);

  public accept(store: AppState): boolean {
    return Checker.isStudentOrResponsible(store?.funcao_padrao);
  }

  public readonly items: Builder[] = [
    () => ({
      title: 'breadcrumb.aluno.avaliacoes',
      icon: 'icon-menu-evaluations',
      style:
        'hover:bg-modules-student-600 bg-modules-student-100 hover:border-modules-student-600 hover:text-white text-modules-student-600',
      iconStyle: 'w-8 h-8',
      url: '/aluno/avaliacoes',
      show: this.store.select(AppSelectors.ActiveUser).pipe(
        notNull(),
        map(
          (activeUser) => activeUser?.boletim_infantil !== StatusEnum.Active && activeUser?.pais_id === PaisEnum.Brasil,
        ),
      ),
    }),
    () => ({
      title: 'geral.notas',
      icon: 'icon-menu-student',
      style:
        'hover:bg-modules-student-600 bg-modules-student-100 hover:border-modules-student-600 hover:text-white text-modules-student-600',
      iconStyle: 'w-8 h-8',
      url: '/aluno/notas-faltas',
      show: this.store.select(AppSelectors.ActiveUser).pipe(
        notNull(),
        map(
          (activeUser) => activeUser?.boletim_infantil !== StatusEnum.Active && activeUser?.pais_id === PaisEnum.Brasil,
        ),
      ),
    }),
    () => ({
      title: 'academico.apontamentos',
      icon: 'icon-menu-notifications',
      style:
        'hover:bg-modules-student-600 bg-modules-student-100 hover:border-modules-student-600 hover:text-white text-modules-student-600',
      iconStyle: 'w-8 h-8',
      url: '/aluno/apontamentos',
      permission: [1, 3],
    }),
    () => ({
      title: 'breadcrumb.aluno.horario',
      icon: 'icon-menu-timetable',
      style:
        'hover:bg-modules-student-600 bg-modules-student-100 hover:border-modules-student-600 hover:text-white text-modules-student-600',
      iconStyle: 'w-8 h-8',
      url: '/aluno/horario-aulas',
      permission: [1, 3],
    }),
    () => ({
      title: 'gestor-avaliacoes.minhas-provas',
      icon: 'icon-gestor-avaliacoes',
      style:
        'hover:bg-modules-assessments-manager-600 bg-modules-assessments-manager-100 hover:border-modules-assessments-manager-600 hover:text-white text-modules-assessments-manager-600',
      iconStyle: 'w-8 h-8',
      permission: [1, 4],
      url: '/eclass-prova',
    }),
    () => ({
      title: 'atalhos.replay',
      icon: 'icon-agenda',
      style:
        'hover:bg-modules-learning-trails-600 bg-modules-learning-trails-100 hover:border-modules-learning-trails-600 hover:text-white text-modules-learning-trails-600',
      iconStyle: 'w-8 h-8',
      permission: [18, 32],
      url: '/trilhas-de-aprendizagem/cursos',
      params: { search: 'replay' },
      show: this.store.select(AppSelectors.ActiveUser).pipe(
        notNull(),
        map(({ padrao_serie_id }) => [60, 70, 80, 90, 100, 110, 120].includes(padrao_serie_id)),
      ),
    }),
    () => ({
      title: 'geral.7edu',
      icon: 'icon-7edu',
      style: 'hover:bg-base-zoom-600 bg-base-zoom-100 hover:border-base-zoom-600 hover:text-white text-base-zoom-600',
      iconStyle: 'w-12 h-6 -rotate-15',
      show: this.store.select(AppSelectors.ActiveUser).pipe(
        notNull(),
        map((activeUser) => activeUser?.secretaria_sistema_id === 3),
      ),
      load: signal(false),
      open: () => this.openAcademicUrl(),
    }),
    () => ({
      title: 'servicos.redacao',
      icon: 'icon-essay',
      style:
        'hover:bg-base-service-chave2-600 bg-base-service-chave2-100 hover:border-base-service-chave2-600 hover:text-white text-base-service-chave2-600',
      iconStyle: 'w-8 h-8',
      load: signal(false),
      open: () => this.openRedacao(),
      show: this.store.select(AppSelectors.ActiveUser).pipe(
        notNull(),
        map((activeUser) => activeUser?.nivel_id === 4 && activeUser?.pais_id === PaisEnum.Brasil),
      ),
    }),
    () => ({
      title: 'menu.jogos',
      icon: 'icon-dice',
      style:
        'hover:bg-modules-games-600 bg-modules-games-100 hover:border-modules-games-600 hover:text-white text-modules-games-600',
      iconStyle: 'w-8 h-8',
      permission: [1, 6],
      url: '/jogos',
      show: this.store.select(AppSelectors.ActiveUser).pipe(
        notNull(),
        map((activeUser) => activeUser?.nivel_id === 2 || activeUser?.pais_id !== PaisEnum.Brasil),
      ),
    }),
    () => ({
      title: 'menu.financeiro',
      icon: 'icon-money',
      style:
        'hover:bg-modules-finance-600 bg-modules-finance-100 hover:border-modules-finance-600 hover:text-white text-modules-finance-600',
      iconStyle: 'w-8 h-8',
      permission: [1, 7],
      open: () => this.openAcademicUrl(),
      load: signal(false),
      show: this.store.select(AppSelectors.ActiveUser).pipe(
        notNull(),
        map((activeUser) => activeUser?.pais_id === PaisEnum.Brasil && !activeUser?.externo),
      ),
    }),
    () => ({
      title: 'breadcrumb.eclass.observacoes',
      url: '/eclass/observacoes',
      icon: 'icon-feedback-line',
      style:
        'hover:bg-modules-eclass-600 bg-modules-eclass-100 hover:border-modules-eclass-600 hover:text-white text-modules-eclass-600',
      iconStyle: 'w-8 h-8',
      permission: [20, 35],
    }),
  ];

  private openAcademicUrl(): Observable<any> {
    return this.store.select(AppSelectors.appFeature).pipe(
      filter((state) => !!state?.usuario_id),
      take(1),
      notNull(),
      switchMap((state) => this.userService.syncLoginIatec(state.usuario_id).pipe(catchError(() => of(null)))),
      switchMap(() => this.userService.loginIatec().pipe(catchError(() => of(null)))),
      tap((url) => window.open(url, '_blank')),
    );
  }

  private openRedacao(): Observable<any> {
    return this.integrationService.estuda().pipe(
      catchError(() =>
        of({ status: false, msg: this.translateService.instant('servicos.erros.contato-administrador') }),
      ),
      switchMap((data) => {
        if (data.status) {
          const windows = window.open('', '_blank');
          if (!windows) {
            const message = this.translateService.instant('servicos.erros.permitir-popup');
            const img = `<br><img src="assets/images/pop-up-links.jpg" class="pt-3">`;
            return this.alertService.alert({ title: 'status.atencao', innerHtml: `${message} ${img}` });
          }
          windows.location.href = data.url;
          return of(true);
        }
        return this.alertService.alert({ title: 'status.atencao', innerHtml: data.msg });
      }),
    );
  }
}
